import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { Trash2, Plus, X, Save, ArrowLeft } from "lucide-react";
import api from "../api/axios";

const API_PATH = "/admin/border-tax";

const CAB_TYPES = [
  "Taxi 4+1",
  "Taxi 5+1",
  "Taxi 6+1",
  "Taxi 7+1",
  "Taxi 12+1",
];

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Delhi", "Jammu and Kashmir", "Ladakh", "Puducherry", "Chandigarh",
  "Andaman and Nicobar Islands", "Dadra and Nagar Haveli and Daman and Diu",
  "Lakshadweep",
];

const CreateOrEditBorderTax = () => {
  const navigate = useNavigate();
  const { id: statePId } = useParams();
  const isEdit = Boolean(statePId);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [canEdit, setCanEdit] = useState(true);

  // multi-state buffers
  const [availableSel, setAvailableSel] = useState([]); // multi-select buffer
  const [manualStates, setManualStates] = useState(""); // comma-separated buffer
  const [checkedStates, setCheckedStates] = useState([]); // remove ke liye ticked

  const [form, setForm] = useState({
    states: [],
    platform: {
      fee: "50",
      quarterly_fee: "100",
      monthlay_fee: "200",
      yearly_fee: "300",
      gateway_fee: "2",
      is_fee_apply: true,
    },
    active: false,
    is_based_onDay: false,
    is_based_onTime: false,
    days_input_show: false,
    TaxMode: [
      { title: "days", active: false, useDaysSlab: true, vehiclePrices: [] },
      { title: "monthly", active: false, vehiclePrices: [] },
      { title: "quarterly", active: false, vehiclePrices: [] },
      { title: "yearly", active: false, vehiclePrices: [] },
    ],
    pricesByDays: [],
    borderEntries: [],
  });

  // Fetch data in edit mode
  useEffect(() => {
    if (isEdit && statePId) fetchData();
  }, [isEdit, statePId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get(`${API_PATH}/${statePId}`);
      if (!res.data?.success) return;

      const data = res.data.data;
      setCanEdit(data.canEdit !== false);

      const cleanedPricesByDays = Array.isArray(data.pricesByDays)
        ? data.pricesByDays.map((cab) => ({
          cabType: cab.cabType || "",
          afterLastDayIncreasePerDayWise: cab.afterLastDayIncreasePerDayWise != null
            ? String(cab.afterLastDayIncreasePerDayWise)
            : "",
          slabs: Array.isArray(cab.slabs)
            ? cab.slabs.map((s) => ({
              days: s.days != null ? String(s.days) : "",
              price: s.price != null ? String(s.price) : "",
            }))
            : [{ days: "1", price: "" }],
        }))
        : [];

      const cleanedTaxMode = Array.isArray(data.TaxMode)
        ? data.TaxMode.map((mode) => ({
          title: mode.title || "",
          active: !!mode.active,
          vehiclePrices: Array.isArray(mode.vehiclePrices)
            ? mode.vehiclePrices.map((vp) => ({
              cabType: vp.cabType || "",
              monthly_price: vp.monthly_price != null ? String(vp.monthly_price) : "",
              quarterly_price: vp.quarterly_price != null ? String(vp.quarterly_price) : "",
              yearly_price: vp.yearly_price != null ? String(vp.yearly_price) : "",
            }))
            : [],
        }))
        : form.TaxMode;

      const cleanedBorderEntries = Array.isArray(data.borderEntries)
        ? data.borderEntries.map((e) => ({
          title: e.title || "",
          isActive: !!e.isActive,
        }))
        : [];

      setForm({
        // backward-compat: purana single `state` bhi array me convert
        states: Array.isArray(data.states)
          ? data.states
          : data.state
          ? [data.state]
          : [],
        platform: data.platform || {
          fee: "50",
          quarterly_fee: "100",
          monthly_fee: "200",
          yearly_fee: "300",
          gateway_fee: "2",
          is_fee_apply: true,
        },
        active: !!data.active,
        is_based_onDay: !!data.is_based_onDay,
        is_based_onTime: !!data.is_based_onTime,
        days_input_show: !!data.days_input_show,
        TaxMode: cleanedTaxMode,
        pricesByDays: cleanedPricesByDays,
        borderEntries: cleanedBorderEntries,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Failed to load",
        text: err.response?.data?.message || "Server error",
      });
    } finally {
      setLoading(false);
    }
  };

  // ── Multi-state helpers ──
  const remainingStates = INDIAN_STATES.filter((s) => !form.states.includes(s));

  const addStates = (list) => {
    const clean = list.map((s) => s.trim()).filter(Boolean);
    if (!clean.length) return;
    setForm((prev) => {
      const set = new Set(prev.states);
      clean.forEach((s) => set.add(s));
      return { ...prev, states: Array.from(set) };
    });
  };

  const addFromSelect = () => {
    addStates(availableSel);
    setAvailableSel([]);
  };

  const addFromManual = () => {
    addStates(manualStates.split(","));
    setManualStates("");
  };

  const toggleCheckState = (s) => {
    setCheckedStates((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    );
  };

  const toggleAllStates = () => {
    setCheckedStates((prev) =>
      prev.length === form.states.length ? [] : [...form.states]
    );
  };

  const removeCheckedStates = async () => {
    const c = await Swal.fire({
      title: `Remove ${checkedStates.length} state(s)?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
    });
    if (!c.isConfirmed) return;
    setForm((prev) => ({
      ...prev,
      states: prev.states.filter((s) => !checkedStates.includes(s)),
    }));
    setCheckedStates([]);
  };

  const removeOneState = (s) => {
    setForm((prev) => ({ ...prev, states: prev.states.filter((x) => x !== s) }));
    setCheckedStates((prev) => prev.filter((x) => x !== s));
  };

  // Cab Pricing Helpers
  const addCabType = () => {
    setForm((prev) => ({
      ...prev,
      pricesByDays: [
        ...prev.pricesByDays,
        { cabType: "", afterLastDayIncreasePerDayWise: "", slabs: [{ days: "1", price: "" }] },
      ],
    }));
  };

  const removeCabType = async (index) => {
    const cab = form.pricesByDays[index];
    const confirmed = await Swal.fire({
      title: "Remove Cab Type?",
      text: `Delete pricing for ${cab.cabType || "this cab"}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
    });
    if (!confirmed.isConfirmed) return;

    setForm((prev) => ({
      ...prev,
      pricesByDays: prev.pricesByDays.filter((_, i) => i !== index),
    }));
  };

  const addSlab = (cabIndex) => {
    setForm((prev) => {
      const updated = [...prev.pricesByDays];
      updated[cabIndex].slabs.push({ days: "", price: "" });
      return { ...prev, pricesByDays: updated };
    });
  };

  const removeSlab = (cabIndex, slabIndex) => {
    setForm((prev) => {
      const updated = [...prev.pricesByDays];
      updated[cabIndex].slabs.splice(slabIndex, 1);
      return { ...prev, pricesByDays: updated };
    });
  };

  // Border Entry Helpers
  const addBorderEntry = () => {
    setForm((prev) => ({
      ...prev,
      borderEntries: [...prev.borderEntries, { title: "", isActive: true }],
    }));
  };

  const removeBorderEntry = (index) => {
    setForm((prev) => ({
      ...prev,
      borderEntries: prev.borderEntries.filter((_, i) => i !== index),
    }));
  };

  // Vehicle Price Helpers
  const updateVehiclePrice = (modeTitle, cabType, field, value) => {
    setForm((prev) => {
      const updatedTaxMode = [...prev.TaxMode];
      const modeIndex = updatedTaxMode.findIndex((m) => m.title === modeTitle);
      if (modeIndex === -1) return prev;

      let vehiclePrices = [...(updatedTaxMode[modeIndex].vehiclePrices || [])];
      const existingIndex = vehiclePrices.findIndex((v) => v.cabType === cabType);

      if (existingIndex !== -1) {
        vehiclePrices[existingIndex] = { ...vehiclePrices[existingIndex], [field]: value };
      } else {
        vehiclePrices.push({ cabType, monthly_price: "", quarterly_price: "", yearly_price: "", [field]: value });
      }

      updatedTaxMode[modeIndex].vehiclePrices = vehiclePrices;
      return { ...prev, TaxMode: updatedTaxMode };
    });
  };

  const addVehicleToMode = (modeTitle) => {
    const availableCabs = form.pricesByDays.map((c) => c.cabType?.trim()).filter(Boolean);
    if (availableCabs.length === 0) {
      Swal.fire("Warning", "Please add at least one cab type in Cab Pricing Rules first.", "warning");
      return;
    }

    setForm((prev) => {
      const updatedTaxMode = [...prev.TaxMode];
      const modeIndex = updatedTaxMode.findIndex((m) => m.title === modeTitle);
      if (modeIndex === -1) return prev;

      const existingCabs = updatedTaxMode[modeIndex].vehiclePrices.map((v) => v.cabType);
      const missingCabs = availableCabs.filter((cab) => !existingCabs.includes(cab));

      if (missingCabs.length === 0) {
        Swal.fire("Info", "All cab types are already added for this mode.", "info");
        return prev;
      }

      const newVehicles = missingCabs.map((cabType) => ({
        cabType,
        monthly_price: "",
        quarterly_price: "",
        yearly_price: "",
      }));

      updatedTaxMode[modeIndex].vehiclePrices = [...updatedTaxMode[modeIndex].vehiclePrices, ...newVehicles];
      return { ...prev, TaxMode: updatedTaxMode };
    });
  };

  const removeVehicleFromMode = (modeTitle, cabType) => {
    setForm((prev) => {
      const updatedTaxMode = [...prev.TaxMode];
      const modeIndex = updatedTaxMode.findIndex((m) => m.title === modeTitle);
      if (modeIndex === -1) return prev;

      updatedTaxMode[modeIndex].vehiclePrices = updatedTaxMode[modeIndex].vehiclePrices.filter(
        (v) => v.cabType !== cabType
      );
      return { ...prev, TaxMode: updatedTaxMode };
    });
  };

  const getVehiclePrices = (modeTitle) => {
    return form.TaxMode.find((m) => m.title === modeTitle)?.vehiclePrices || [];
  };

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.states.length)
      return Swal.fire("Required", "Add at least one state", "warning");
    if (form.pricesByDays.length === 0) return Swal.fire("Incomplete", "Add at least one cab type", "warning");

    if (isEdit && !canEdit) {
      return Swal.fire("Not allowed", "You don't have permission to edit this border tax", "error");
    }

    setSubmitting(true);
    try {
      const url = isEdit ? `${API_PATH}/${statePId}` : API_PATH;
      const method = isEdit ? "put" : "post";
      await api[method](url, form);

      Swal.fire({
        icon: "success",
        title: isEdit ? "Updated" : "Created",
        text: `Border tax ${isEdit ? "updated" : "created"} successfully`,
        timer: 1800,
      });
      navigate("/border-tax");
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: err.response?.data?.message || "Something went wrong",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Render Vehicle Prices
  const renderVehiclePrices = (modeTitle, label) => {
    const currentPrices = getVehiclePrices(modeTitle);
    const fieldName = modeTitle === "monthly" ? "monthly_price" : modeTitle === "quarterly" ? "quarterly_price" : "yearly_price";

    return (
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-lg text-gray-800">{label}</h3>
          <button
            type="button"
            onClick={() => addVehicleToMode(modeTitle)}
            className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
          >
            <Plus size={18} /> Add Vehicle
          </button>
        </div>

        {currentPrices.length === 0 ? (
          <div className="bg-amber-50 border border-amber-200 p-6 rounded-xl text-center text-amber-700">
            No vehicles added. Click "Add Vehicle" to configure prices.
          </div>
        ) : (
          currentPrices.map((vehicle) => (
            <div key={vehicle.cabType} className="flex items-center gap-6 bg-white p-5 rounded-xl border border-gray-200">
              <div className="font-medium text-gray-700 w-48">{vehicle.cabType}</div>
              <div className="flex-1 max-w-xs">
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                  <input
                    type="number"
                    min="0"
                    value={vehicle[fieldName] || ""}
                    onChange={(e) => updateVehiclePrice(modeTitle, vehicle.cabType, fieldName, e.target.value)}
                    className="w-full pl-11 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder="0"
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={() => removeVehicleFromMode(modeTitle, vehicle.cabType)}
                className="text-red-600 hover:bg-red-50 p-2 rounded"
              >
                <Trash2 size={22} />
              </button>
            </div>
          ))
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin h-10 w-10 border-4 border-indigo-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-600">Loading data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {isEdit ? "Edit Border Tax" : "Create New Border Tax"}
            </h1>
            <p className="mt-1 text-gray-600">
              {isEdit ? form.states.join(", ") : "Configure border tax for vehicles"}
            </p>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 text-gray-700 transition"
          >
            <ArrowLeft size={18} /> Back
          </button>
        </div>

        {isEdit && !canEdit && (
          <div className="mb-6 bg-amber-50 border border-amber-300 text-amber-800 px-5 py-3 rounded-xl text-sm font-medium">
            You don't have permission to edit this border tax. You can view it but changes won't be saved.
          </div>
        )}

        <fieldset disabled={isEdit && !canEdit}>
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* 1. States (multi add / bulk remove) */}
          <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <label className="block text-sm font-medium text-gray-700">
                States <span className="text-red-500">*</span>
                <span className="ml-2 text-xs text-gray-400">
                  ({form.states.length} added)
                </span>
              </label>
              {checkedStates.length > 0 && (
                <button
                  type="button"
                  onClick={removeCheckedStates}
                  className="flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition"
                >
                  <Trash2 size={16} /> Remove Selected ({checkedStates.length})
                </button>
              )}
            </div>

            {/* Add row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
              {/* multi-select from list */}
              <div>
                <p className="text-xs text-gray-500 mb-1">
                  Pick from list (Ctrl/Shift = multi-select)
                </p>
                <select
                  multiple
                  size={6}
                  value={availableSel}
                  onChange={(e) =>
                    setAvailableSel(
                      Array.from(e.target.selectedOptions, (o) => o.value)
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
                >
                  {remainingStates.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={addFromSelect}
                  disabled={availableSel.length === 0}
                  className="mt-2 flex items-center gap-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                  <Plus size={16} /> Add Selected ({availableSel.length})
                </button>
              </div>

              {/* manual comma-separated */}
              <div>
                <p className="text-xs text-gray-500 mb-1">
                  Or type (comma separated)
                </p>
                <textarea
                  rows={4}
                  value={manualStates}
                  onChange={(e) => setManualStates(e.target.value)}
                  placeholder="e.g. Uttar Pradesh, Bihar, Rajasthan"
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
                />
                <button
                  type="button"
                  onClick={addFromManual}
                  className="mt-2 flex items-center gap-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  <Plus size={16} /> Add
                </button>
              </div>
            </div>

            {/* Added list with checkboxes */}
            {form.states.length === 0 ? (
              <div className="text-center py-8 text-gray-500 italic border-2 border-dashed border-gray-300 rounded-2xl">
                No states added yet
              </div>
            ) : (
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <div className="flex items-center gap-3 bg-gray-50 px-4 py-3 border-b">
                  <input
                    type="checkbox"
                    checked={
                      checkedStates.length === form.states.length &&
                      form.states.length > 0
                    }
                    onChange={toggleAllStates}
                    className="h-4 w-4 text-indigo-600"
                  />
                  <span className="text-sm font-medium text-gray-600">
                    Select All
                  </span>
                </div>
                <div className="max-h-64 overflow-y-auto divide-y">
                  {form.states.map((s) => (
                    <div
                      key={s}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        checked={checkedStates.includes(s)}
                        onChange={() => toggleCheckState(s)}
                        className="h-4 w-4 text-indigo-600"
                      />
                      <span className="flex-1 text-gray-800">{s}</span>
                      <button
                        type="button"
                        onClick={() => removeOneState(s)}
                        className="text-gray-400 hover:text-red-600"
                      >
                        <X size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* 2. Border Tax Status */}
          <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-6">
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <p className="font-semibold text-gray-900">Border Tax Status</p>
                <p className="text-sm text-gray-500">Enable or disable this Border Tax</p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`px-4 py-1 text-xs font-semibold rounded-full ${form.active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                  {form.active ? "Active" : "Inactive"}
                </span>
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm((prev) => ({ ...prev, active: e.target.checked }))}
                  className="peer sr-only"
                />
                <div className={`w-12 h-7 flex items-center rounded-full p-1 transition ${form.active ? "bg-green-600" : "bg-gray-300"}`}>
                  <div className={`bg-white w-5 h-5 rounded-full shadow transition ${form.active ? "translate-x-5" : "translate-x-0"}`} />
                </div>
              </div>
            </label>
          </div>

          {/* 3. Platform Fee */}
          <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-5">Platform Fee</h2>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.platform.is_fee_apply}
                onChange={(e) => setForm({ ...form, platform: { ...form.platform, is_fee_apply: e.target.checked } })}
                className="h-5 w-5 text-indigo-600"
              />
              <span className="text-gray-800 font-medium">Apply platform fee to all bookings in this state</span>
            </label>

            {form.platform.is_fee_apply && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pl-8 mt-6">

                {/* Flat Fee */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Flat Fee (₹)
                  </label>
                  <div className="relative max-w-xs">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">
                      ₹
                    </span>
                    <input
                      type="number"
                      value={form.platform.fee}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          platform: { ...form.platform, fee: e.target.value },
                        })
                      }
                      className="w-full pl-11 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                {/* Quarterly Fee */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quarterly Fee (₹)
                  </label>
                  <div className="relative max-w-xs">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">
                      ₹
                    </span>
                    <input
                      type="number"
                      value={form.platform.quarterly_fee}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          platform: { ...form.platform, quarterly_fee: e.target.value },
                        })
                      }
                      className="w-full pl-11 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                {/* Monthly Fee */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Monthly Fee (₹)
                  </label>
                  <div className="relative max-w-xs">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">
                      ₹
                    </span>
                    <input
                      type="number"
                      value={form.platform.monthly_fee}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          platform: { ...form.platform, monthly_fee: e.target.value },
                        })
                      }
                      className="w-full pl-11 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                {/* Yearly Fee */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Yearly Fee (₹)
                  </label>
                  <div className="relative max-w-xs">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">
                      ₹
                    </span>
                    <input
                      type="number"
                      value={form.platform.yearly_fee}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          platform: { ...form.platform, yearly_fee: e.target.value },
                        })
                      }
                      className="w-full pl-11 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                {/* Gateway Fee */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Gateway Fee (%)
                  </label>
                  <div className="relative max-w-xs">
                    <input
                      type="number"
                      value={form.platform.gateway_fee}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          platform: { ...form.platform, gateway_fee: e.target.value },
                        })
                      }
                      className="w-full py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">
                      %
                    </span>
                  </div>
                </div>

              </div>
            )}
          </div>

          {/* 4. Calculation Mode */}
          <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-5">Pricing Calculation Mode</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <label className="flex items-center gap-3 bg-gray-50 p-5 rounded-xl border cursor-pointer hover:border-indigo-300">
                <input
                  type="checkbox"
                  checked={form.is_based_onDay}
                  onChange={(e) => setForm({ ...form, is_based_onDay: e.target.checked })}
                  className="h-5 w-5 text-indigo-600"
                />
                <div>
                  <div className="font-medium">Based on Days</div>
                  <div className="text-sm text-gray-500">Price calculated per day / slab</div>
                </div>
              </label>

              <label className="flex items-center gap-3 bg-gray-50 p-5 rounded-xl border cursor-pointer hover:border-indigo-300">
                <input
                  type="checkbox"
                  checked={form.is_based_onTime}
                  onChange={(e) => setForm({ ...form, is_based_onTime: e.target.checked })}
                  className="h-5 w-5 text-indigo-600"
                />
                <div>
                  <div className="font-medium">Based on Time</div>
                  <div className="text-sm text-gray-500">Price calculated per hour / minute</div>
                </div>
              </label>
            </div>
          </div>

          {/* 5. Cab Pricing Rules */}
          <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Cab Pricing Rules (Days Slab)</h2>
              <button
                type="button"
                onClick={addCabType}
                className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition"
              >
                <Plus size={20} /> Add Cab Type
              </button>
            </div>

            {form.pricesByDays.length === 0 ? (
              <div className="text-center py-12 text-gray-500 italic border-2 border-dashed border-gray-300 rounded-2xl">
                No cab types added yet. Click "Add Cab Type" to begin.
              </div>
            ) : (
              <div className="space-y-8">
                {form.pricesByDays.map((cab, cabIdx) => (
                  <div key={cabIdx} className="border border-gray-200 rounded-2xl p-6 bg-gray-50">
                    <div className="flex justify-between items-center mb-6 pb-4 border-b">
                      <select
                        value={cab.cabType}
                        onChange={(e) => {
                          const updated = [...form.pricesByDays];
                          updated[cabIdx].cabType = e.target.value;
                          setForm({ ...form, pricesByDays: updated });
                        }}
                        className="w-64 px-5 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
                        required
                      >
                        <option value="">Select cab type...</option>
                        {CAB_TYPES.map((type) => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>

                      <button type="button" onClick={() => removeCabType(cabIdx)} className="text-red-600 hover:bg-red-50 p-3 rounded-xl">
                        <Trash2 size={24} />
                      </button>
                    </div>

                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Price increase per extra day (after last slab)
                      </label>
                      <div className="relative max-w-xs">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                        <input
                          type="number"
                          value={cab.afterLastDayIncreasePerDayWise}
                          onChange={(e) => {
                            const updated = [...form.pricesByDays];
                            updated[cabIdx].afterLastDayIncreasePerDayWise = e.target.value;
                            setForm({ ...form, pricesByDays: updated });
                          }}
                          className="w-full pl-11 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
                          placeholder="e.g. 120"
                        />
                      </div>
                    </div>

                    <div>
                      <div className="font-medium text-gray-700 mb-3">Pricing Slabs</div>
                      {cab.slabs.map((slab, slabIdx) => (
                        <div key={slabIdx} className="flex items-center gap-4 mb-4">
                          <input
                            type="number"
                            min="1"
                            value={slab.days}
                            onChange={(e) => {
                              const updated = [...form.pricesByDays];
                              updated[cabIdx].slabs[slabIdx].days = e.target.value;
                              setForm({ ...form, pricesByDays: updated });
                            }}
                            className="w-24 text-center px-4 py-3 border border-gray-300 rounded-xl"
                          />
                          <span className="text-gray-500">days → ₹</span>
                          <input
                            type="number"
                            value={slab.price}
                            onChange={(e) => {
                              const updated = [...form.pricesByDays];
                              updated[cabIdx].slabs[slabIdx].price = e.target.value;
                              setForm({ ...form, pricesByDays: updated });
                            }}
                            className="flex-1 max-w-[180px] px-4 py-3 border border-gray-300 rounded-xl"
                          />
                          <button
                            type="button"
                            onClick={() => removeSlab(cabIdx, slabIdx)}
                            className="text-gray-400 hover:text-red-600"
                          >
                            <X size={22} />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => addSlab(cabIdx)}
                        className="text-indigo-600 hover:text-indigo-700 text-sm flex items-center gap-1 mt-2"
                      >
                        <Plus size={16} /> Add Slab
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 6. Tax Mode Options (Vehicle Wise) - With Add Button */}
          <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-8">Tax Mode Options (Vehicle Wise)</h2>

            <div className="space-y-12">
              {form.TaxMode.map((mode) =>
                mode.title !== "days" ? (
                  <div key={mode.title} className="bg-gray-50 p-7 rounded-2xl border border-gray-200">
                    <div className="flex justify-between items-center mb-6">
                      <div className="font-semibold text-2xl capitalize">{mode.title}</div>
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={mode.active}
                          onChange={(e) => {
                            const updated = [...form.TaxMode];
                            const idx = updated.findIndex((m) => m.title === mode.title);
                            updated[idx].active = e.target.checked;
                            setForm({ ...form, TaxMode: updated });
                          }}
                          className="h-5 w-5 text-indigo-600"
                        />
                        <span className="font-medium">Active</span>
                      </label>
                    </div>

                    {mode.active ? (
                      renderVehiclePrices(mode.title, `${mode.title.charAt(0).toUpperCase() + mode.title.slice(1)} Tax`)
                    ) : (
                      <p className="text-gray-500 italic py-8 text-center">
                        Activate this mode to configure vehicle-wise prices
                      </p>
                    )}
                  </div>
                ) : null
              )}
            </div>
          </div>

          {/* 7. Border Entries */}
          <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Border Checkpoints</h2>
              <button
                type="button"
                onClick={addBorderEntry}
                className="flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition"
              >
                <Plus size={18} /> Add Checkpoint
              </button>
            </div>

            {form.borderEntries.length === 0 ? (
              <div className="text-center py-12 text-gray-500 italic border-2 border-dashed border-gray-300 rounded-2xl">
                No border checkpoints added yet
              </div>
            ) : (
              <div className="space-y-3">
                {form.borderEntries.map((entry, idx) => (
                  <div key={idx} className="flex items-center gap-3 bg-gray-50 px-5 py-4 rounded-xl border">
                    <input
                      type="text"
                      value={entry.title}
                      onChange={(e) => {
                        const updated = [...form.borderEntries];
                        updated[idx].title = e.target.value;
                        setForm({ ...form, borderEntries: updated });
                      }}
                      placeholder="e.g. AGRA"
                      className="flex-1 px-5 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => removeBorderEntry(idx)}
                      className="text-red-600 hover:bg-red-50 p-3 rounded-xl"
                    >
                      <Trash2 size={22} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 pt-6">
            <button
              type="submit"
              disabled={submitting || (isEdit && !canEdit)}
              className="flex-1 flex items-center justify-center gap-3 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-4 rounded-2xl text-lg transition"
            >
              <Save size={22} />
              {submitting ? "Saving..." : isEdit ? "Update Border Tax" : "Create Border Tax"}
            </button>

            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex-1 py-4 border border-gray-300 text-gray-700 font-semibold rounded-2xl hover:bg-gray-50 transition"
            >
              Cancel
            </button>
          </div>
        </form>
        </fieldset>
      </div>
    </div>
  );
};

export default CreateOrEditBorderTax;